import { BaseApiService } from './base-api.service';
import { CXEnvironments } from '~/types/cx-types/cx-environment.types';
import { v4 as uuidv4 } from 'uuid';
import type { User } from '~/models/user.model';
import { AxiosRequestConfig } from 'axios';
import { isViewerMode } from '~/utils/viewer-mode-flow.utils';
import { trackError } from '~/services/logger/error.utils';
import { AuthenticatedGlobalContextValue } from '~/providers/base-global-context/authenticated-global.context';

export const BROWSER_TAB_ID_HEADER_KEY = 'hb-browser-tab-id';

const IGNORE_REQUEST_URLS: Array<string> = [
    'https://api.cloudinary.com',
    'https://res.cloudinary.com'
];

export class ApiService extends BaseApiService {
    globalContext: AuthenticatedGlobalContextValue;

    constructor(globalContext: AuthenticatedGlobalContextValue) {
        super(globalContext);

        this.globalContext = globalContext;
    }

    protected getBaseUrl() {
        const {
            configStore: {
                config: { api_urls_prefix, url }
            },
            // @ts-ignore
            clientPortalConfigStore
        } = this.globalContext;

        // if this is defined, it means we're in a CX context
        if (clientPortalConfigStore && clientPortalConfigStore.env) {
            if (
                clientPortalConfigStore.env.environment ===
                CXEnvironments.Development
            ) {
                return '/';
            }

            return clientPortalConfigStore.apiBaseUrl;
        }

        return api_urls_prefix || url;
    }

    protected requestInterceptor({
        url,
        params,
        headers: overrideHeaders,
        ...config
    }: AxiosRequestConfig): AxiosRequestConfig {
        const { sessionStore, userStore } = this.globalContext;
        const { currCompanyId } = sessionStore;

        const ignore = this.shouldIgnore(url);

        const headers = this.getHeaders();
        const duplicateCallsPreventionHeaders = ignore
            ? {}
            : this.getDuplicateCallsPreventionHeaders();

        const jointHeaders = {
            ...(overrideHeaders || {}),
            ...headers,
            ...duplicateCallsPreventionHeaders
        };

        const currUser = userStore.byId(sessionStore.currUserId);
        if (currUser && !ignore && url.indexOf('/api/v2') !== -1) {
            params.ctxu = currUser._id;
            params.ctxc = currCompanyId;

            if (currUser.isInBookkeeperMode) {
                params.auditee_company_id = currUser.bookkeeperModeCompanyId;
            }
        }

        return { ...config, url, params, headers: jointHeaders };
    }

    protected shouldIgnore(url: string) {
        return IGNORE_REQUEST_URLS.some(urlToIgnore => {
            return url.startsWith(urlToIgnore);
        });
    }

    getHeaders() {
        const {
            configStore: {
                config: {
                    hb_api_headers: {
                        api_client_version,
                        weak_auth_hash,
                        weak_auth_user_id,
                        weak_auth_user_email,
                        fingerprint
                    },
                    api_version
                }
            },
            userStore,
            sessionStore,
            storageService
        } = this.globalContext;
        const baseHeaders = {
            [BROWSER_TAB_ID_HEADER_KEY]: sessionStore.browserTabId,
            [fingerprint]: userStore.fingerprint
        };
        try {
            const currUser = userStore.byId(sessionStore.currUserId);
            if (sessionStore.authenticated) {
                return {
                    ...baseHeaders,
                    ...this.getStrongUserHeaderCredentials(currUser)
                };
            }
            if (isViewerMode()) {
                return { ...baseHeaders, [api_client_version]: api_version };
            }
            if (
                sessionStore.weakAuthenticated ||
                (sessionStore.weakAuthUser && sessionStore.isAuthenticating)
            ) {
                const weakAuthData = JSON.parse(
                    storageService.getItem(sessionStore.limitedAuthStorageKey)
                );
                return {
                    ...baseHeaders,
                    [api_client_version]: api_version,
                    [weak_auth_hash]: weakAuthData?.hash,
                    ...(weakAuthData?.email
                        ? { [weak_auth_user_email]: weakAuthData.email }
                        : {}),
                    ...(weakAuthData?._id
                        ? { [weak_auth_user_id]: weakAuthData._id }
                        : {})
                };
            }
        } catch (error) {
            trackError({
                error,
                message: 'failed at setting auth headers'
            });
            return { ...baseHeaders };
        }
        return { ...baseHeaders };
    }

    getStrongUserHeaderCredentials(user: Partial<User> | User) {
        const { configStore, userStore } = this.globalContext;
        const {
            config: {
                hb_api_headers: {
                    api_client_version,
                    auth_user_id,
                    auth_token,
                    admin_token,
                    admin_login,
                    is_temp_pass,
                    fingerprint
                },
                api_version
            }
        } = configStore;

        return user
            ? {
                  [api_client_version]: api_version,
                  [auth_user_id]: user._id,
                  [auth_token]: user.authentication_token,
                  [admin_login]: user.is_hb_admin,
                  [admin_token]: user.admin_token,
                  [is_temp_pass]: user.is_temp_pass_login,
                  [fingerprint]: userStore.fingerprint
              }
            : {};
    }

    protected getDuplicateCallsPreventionHeaders() {
        const { configStore } = this.globalContext;
        const {
            config: {
                hb_api_headers: { duplicate_calls_prevention }
            }
        } = configStore;

        return {
            [duplicate_calls_prevention]: uuidv4()
        };
    }
}
